import { useCallback } from "react"

import type { useSegmentAnalysis } from "./use-segment-analysis"

import { useSegmentAnalysisPortfolioQuery } from "./use-segment-analysis-queries"
import { canEditSegmentById } from "../utils/segment-permissions"

type SegmentAnalysisModel = ReturnType<typeof useSegmentAnalysis>

/**
 * Per-segment edit/run/decide permission for drill-ins (dossier, CCP, Competitor
 * Plan). They are opened for a concrete segment — from the Map/Companies views
 * usually NOT the one selected in the grid — so they must resolve permission from
 * their own segment id, never from `model.canEditSelectedSegment`. Same rule as
 * the backend's ``can_edit_segment``; the backend stays the authority.
 *
 * The display name (legacy name-match ownership) comes from the same cached
 * portfolio query the model reads (react-query dedupes it), or from the demo
 * persona in mock mode — mirroring how the model resolves `canEditManualMetrics`.
 */
export function useSegmentEditPermission(model: SegmentAnalysisModel) {
  const { allSegments, currentUserOid, dataSource, effectiveRole, state } = model
  const portfolioPayload = useSegmentAnalysisPortfolioQuery(!state.useMockData).data
  const ownershipName =
    state.useMockData || dataSource !== "backend"
      ? state.persona
      : (portfolioPayload?.currentUserName ?? null)

  return useCallback(
    (segmentId: string | null | undefined) =>
      canEditSegmentById({
        segmentId,
        segments: allSegments,
        role: effectiveRole,
        currentUserOid,
        currentUserName: ownershipName,
      }),
    [allSegments, effectiveRole, currentUserOid, ownershipName]
  )
}
